import React, { useState, useEffect } from 'react';
import CountUp from 'react-countup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLaptopCode, faDesktop, faMobileAlt, faCogs, faCloud, faBullhorn } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion'; // Added framer-motion for animations
import { Link } from 'react-router-dom'; 
import blog1 from '../assets/blog1.jpg';
import blog2 from '../assets/blog2.jpg';
import blog3 from '../assets/blog3.jpg';
import technologyImg from '../assets/bg-1.jpg';
import businessImg from '../assets/bg-2.jpg';
import digitalImg from '../assets/bg-3.jpg';
import companyImage from '../assets/about1.png';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [
    {
      title: "Empowering Your Digital Future with Gravionix Technologies",
      description: "At Gravionix Technologies, we believe in shaping the future through innovative IT solutions that empower businesses to stay ahead in a rapidly evolving digital world.",
      buttonText: "Get Started",
      buttonLink: "about",
      imageUrl: technologyImg,
    },
    {
      title: "Transforming Ideas into Cutting-Edge Solutions – Gravionix Technologies",
      description: " Our team of passionate technologists and strategic thinkers work closely with businesses to understand their unique challenges and goals.",
      buttonText: "Get Started",
      buttonLink: "about",
      imageUrl: businessImg,
    },
    {
      title: "Your Path to Innovation Begins with Gravionix Technologies",
      description: "At Gravionix Technologies, we help you navigate the evolving digital landscape by delivering innovative, customized technology solutions.",
      buttonText: "Explore Now",
      buttonLink: "about",
      imageUrl: digitalImg,
    },
  ];

  const handleNextSlide = () => {
    setCurrentSlide((prevSlide) => (prevSlide + 1) % slides.length);
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prevSlide) => (prevSlide === 0 ? slides.length - 1 : prevSlide - 1));
  };

  useEffect(() => {
    const interval = setInterval(handleNextSlide, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative overflow-hidden">
      <div
        className="w-full h-screen bg-cover bg-center transition-all duration-1000"
        style={{
          backgroundImage: `url(${slides[currentSlide].imageUrl})`,
        }}
      >
        <motion.div
          className="flex flex-col items-center justify-center h-full bg-black bg-opacity-50 px-6 md:px-12 text-center text-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold">{slides[currentSlide].title}</h2>
          <p className="mt-4 text-lg">{slides[currentSlide].description}</p>
          <a
            href={slides[currentSlide].buttonLink}
            className="mt-6 inline-block bg-blue-800 text-white py-2 px-6 rounded-full hover:bg-black transition"
          >
            {slides[currentSlide].buttonText}
          </a>
        </motion.div>

        {/* Desktop Carousel Navigation */}
        <div className="absolute top-80 left-4 transform -translate-y-1/2 md:top-80 md:left-4 md:transform-none z-10">
          <button
            onClick={handlePrevSlide}
            className="text-white bg-transparent p-3 rounded-full shadow-lg hover:bg-blue-800 transition"
          >
            &#8249;
          </button>
        </div>
        <div className="absolute top-80 right-4 transform -translate-y-1/2 md:top-80 md:right-4 md:transform-none z-10">
          <button
            onClick={handleNextSlide}
            className="text-white bg-transparent p-3 rounded-full shadow-lg hover:bg-blue-800 transition"
          >
            &#8250;
          </button>
        </div>
      </div>

      {/* About Our Company Section */}
      <section className="bg-gray-100 py-16">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              whileInView={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: -100 }}
              transition={{ duration: 1 }}
              viewport={{ once: false }}
            >
              <h6 className="text-xl font-normal mb-4 text-blue-800">About Our Company</h6>
              <h2 className="text-3xl font-bold mb-4">Driving Digital Transformation with Gravionix technologies</h2>
              <p className="text-lg text-gray-700 mb-6">
              Gravionix technologies is dedicated to providing cutting-edge technology and IT solutions to help businesses
                achieve their goals. We specialize in offering tailored IT services that optimize efficiency, productivity,
                and growth. With a passion for innovation, we empower businesses to thrive in the digital era.
              </p>
              <a
                href="/about"
                className="inline-block bg-blue-800 text-white py-3 px-8 rounded-full hover:bg-slate-950 hover:text-white transition"
              >
                Learn More
              </a>
            </motion.div>

            <div className="mt-8 md:mt-0">
              <img
                src={companyImage}
                alt="Company"
                className="w-full h-auto rounded-lg shadow-lg mb-6"
              />
              <motion.div
                className="text-lg text-gray-700"
                whileInView={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 1 }}
                viewport={{ once: false }}
              >
                <h3 className="text-2xl font-semibold mb-6 text-blue-800">Our Achievements</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-center">
                  <motion.div
                    className="p-6 bg-blue-100 rounded-lg shadow-lg hover:shadow-2xl transition"
                    whileInView={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 50 }}
                    transition={{ duration: 1 }}
                  >
                    <h4 className="text-4xl font-bold text-blue-700 mb-2">
                      <CountUp start={0} end={129} duration={2} separator="," />
                      <span className="text-xl">+</span>
                    </h4>
                    <p className="text-gray-600">Projects Completed</p>
                  </motion.div>
                  <motion.div
                    className="p-6 bg-blue-100 rounded-lg shadow-lg hover:shadow-2xl transition"
                    whileInView={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 50 }}
                    transition={{ duration: 1 }}
                  >
                    <h4 className="text-4xl font-bold text-blue-700 mb-2">
                      <CountUp start={0} end={50} duration={2} separator="," />
                      <span className="text-xl">+</span>
                    </h4>
                    <p className="text-gray-600">Happy Customers</p>
                  </motion.div>
                  <motion.div
                    className="p-6 bg-blue-100 rounded-lg shadow-lg hover:shadow-2xl transition"
                    whileInView={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 50 }}
                    transition={{ duration: 1 }}
                  >
                    <h4 className="text-4xl font-bold text-blue-700 mb-2">
                      <CountUp start={0} end={30} duration={2} separator="," />
                      <span className="text-xl">+</span>
                    </h4>
                    <p className="text-gray-600">Awards & Recognition</p>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

     {/* Our Services Section */}
<section className="py-16 bg-white">
  <div className="container mx-auto px-6">
    <h6 className="text-xl font-normal mb-4 text-blue-800 text-center">Our Services</h6>
    <h2 className="text-3xl font-bold mb-8 text-center">Transforming Your Business with Our Expert Services</h2>
    <p className="text-base text-gray-700 mb-12 text-center">
      At Gravionix Technologies, we offer a range of services designed to drive your business forward. Our team of experts is
      ready to assist with technology solutions tailored to your needs, ensuring your success in a digital-first world.
    </p>

    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-12">
      {[
        { service: 'Software Development', description: 'We develop custom software solutions tailored to your business needs. From desktop applications to enterprise software, we ensure seamless integration and scalability.' },
        { service: 'Web Development', description: 'Our web development services focus on creating responsive, secure, and user-friendly websites. We build sites that not only look great but also provide excellent performance.' },
        { service: 'App Development', description: 'We specialize in mobile app development for both Android and iOS platforms, ensuring high-quality, scalable, and secure mobile experiences for your users.' },
        { service: 'Blockchain Development', description: 'We provide blockchain technology solutions, enabling your business to leverage the benefits of decentralized systems, enhanced security, and transparency.' },
        { service: 'Digital Marketing', description: 'Our digital marketing services help businesses grow their online presence through SEO, paid advertising, social media management, and content marketing strategies.' },
        { service: 'SEO', description: 'We offer comprehensive SEO services to improve your website’s ranking on search engines, driving organic traffic and increasing your visibility online.' },
      ].map((service, index) => (
        <motion.div
          className="p-8 bg-blue-100 rounded-lg shadow-lg hover:shadow-2xl transition-transform transform hover:scale-105 hover:translate-y-[-10px] flex flex-col items-center justify-center" // Increased padding to make the box taller
          whileInView={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 50 }}
          transition={{ duration: 1, delay: index * 0.2 }}
          viewport={{ once: false }}
          key={index}
        >
          <FontAwesomeIcon
            icon={[
              faLaptopCode, faDesktop, faMobileAlt, faCogs, faCloud, faBullhorn
            ][index]}
            className="text-3xl sm:text-4xl text-blue-800 mb-4 mx-auto"
          />
          <h4 className="text-xl sm:text-2xl font-semibold mb-4 text-center">{service.service}</h4>
          <p className="text-sm text-gray-600 text-center">
            {service.description}
          </p>
        </motion.div>
      ))}
    </div>
  </div>
</section>


     {/* Latest Blog Section */}
<section className="py-16 bg-gray-100">
  <div className="container mx-auto px-6">
    <h6 className="text-xl font-normal mb-2 text-blue-800 text-center">Latest Blog</h6>
    <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">Stay Updated with Our Latest Insights</h2>
    <p className="text-lg text-gray-700 mb-12 text-center">
      Explore our latest blogs, where we share valuable insights, trends, and tips to help you grow your business and leverage technology.
    </p>

    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-12">
      {/* Blog Data */}
      {[{
        image: blog1,
        title: 'Mastering Digital Marketing Strategies for 2025',
        description: 'Explore the latest digital marketing strategies, from SEO to social media, and learn how to effectively boost your brand visibility and engagement.',
        date: 'January 16, 2025',
        author: 'Ravi Kumar',
        link: '/blog',
      }, {
        image: blog2,
        title: 'The Future of Web Development: Trends & Technologies',
        description: 'Stay ahead of the curve with insights into emerging web development trends such as JAMstack, serverless architecture, front-end technologies.',
        date: 'January 10, 2025',
        author: 'Priya Sharma',
        link: '/blog',
      }, {
        image: blog3,
        title: 'Building Scalable and Robust Mobile Apps in 2025',
        description: 'Learn how to develop mobile apps that are scalable, robust, and user-friendly using the latest tools and technologies in the app development world.',
        date: 'January 5, 2025',
        author: 'Amit Patel',
        link: '/blog',
      }].map((blog, index) => (
        <motion.div
          className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform transform hover:scale-105 hover:translate-y-[-10px]"
          whileInView={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 50 }}
          transition={{ duration: 1, delay: index * 0.2 }}
          viewport={{ once: false }}
          key={index}
        >
          <div className="relative">
            <img
              src={blog.image}
              alt={`Blog ${index + 1}`}
              className="w-full h-60 object-cover rounded-t-lg mb-4"
            />
            <span className="absolute -bottom-4 left-2 bg-blue-900 text-white px-4 py-1 rounded-full font-semibold">
              {blog.date}
            </span>
          </div>
          <div className="p-6">
            <h4 className="text-2xl font-semibold text-gray-800 mb-4">{blog.title}</h4>
            <p className="text-sm text-gray-600 mb-4">
              {blog.description}
            </p>
            <span className="text-gray-600 text-sm mb-6 block">By {blog.author}</span>
            <a href={blog.link} className="text-blue-700 font-semibold hover:underline">Read More</a>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
</section>


   {/* Third Container: Why Choose Us */}
<div className="mt-10 mb-14 px-6 text-white "> {/* Added bg-gray-100 for a light background */}
  <h2 className="text-2xl sm:text-4xl font-bold text-center mb-6 text-black">
    Why Choose Us?
  </h2>
  <p className="text-lg sm:text-xl text-center mb-10 text-gray-500 max-w-3xl mx-auto">
    Discover the reasons that set us apart. We combine creativity, technology, and expertise to deliver outstanding results.
  </p>

  {/* New Layout with Organic Blob Shape for All Reasons */}
  <div className="flex flex-wrap justify-center gap-12 sm:gap-8 lg:gap-12">
    {/* Reason 1 */}
    <motion.div
      className="relative w-full sm:w-72 md:w-80 lg:w-72 h-72 bg-sky-700 rounded-full p-8 shadow-2xl overflow-hidden flex items-center justify-center mb-8 sm:mb-0"
      whileHover={{
        scale: 1.2,
        rotate: 5,
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
      }}
      whileTap={{
        scale: 1.3,
        rotate: 10,
        boxShadow: "0 15px 40px rgba(0, 0, 0, 0.3)",
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 20,
      }}
    >
      <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-50"></div>
      <div className="relative z-10 text-center text-white">
      <h4 className="text-xl font-semibold mb-4 border-b-2 border-white pb-2 inline-block">Expert Team</h4> {/* Only the heading has the underline */}
        <p className="text-lg mb-4 ">
          Our experts bring years of experience, delivering the best IT solutions for your business.
        </p>
        <Link to="/about" className="bg-blue-300 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-all mt-4">
          Learn More
        </Link>
      </div>
    </motion.div>

    {/* Reason 2 */}
    <motion.div
      className="relative w-full sm:w-72 md:w-80 lg:w-72 h-72 bg-sky-700 rounded-full p-8 shadow-2xl overflow-hidden flex items-center justify-center mb-8 sm:mb-0"
      whileHover={{
        scale: 1.2,
        rotate: -5,
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
      }}
      whileTap={{
        scale: 1.3,
        rotate: -10,
        boxShadow: "0 15px 40px rgba(0, 0, 0, 0.3)",
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 20,
      }}
    >
      <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-50 "></div>
      <div className="relative z-10 text-center text-white">
      <h4 className="text-xl font-semibold mb-4 border-b-2 border-white pb-2 inline-block">Tailored Solutions</h4> {/* Added border-b-2 for underline */}
        <p className="text-lg mb-4">
          We design personalized solutions that perfectly align with your unique business needs.
        </p>
        <Link to="/about" className="bg-blue-300 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-all mt-4">
          Learn More
        </Link>
      </div>
    </motion.div>

    {/* Reason 3 */}
    <motion.div
      className="relative w-full sm:w-72 md:w-80 lg:w-72 h-72 bg-sky-700 rounded-full p-8 shadow-2xl overflow-hidden flex items-center justify-center"
      whileHover={{
        scale: 1.2,
        rotate: 5,
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
      }}
      whileTap={{
        scale: 1.3,
        rotate: 10,
        boxShadow: "0 15px 40px rgba(0, 0, 0, 0.3)",
      }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 20,
      }}
    >
      <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-50 "></div>
      <div className="relative z-10 text-center text-white">
      <h4 className="text-xl font-semibold mb-4 border-b-2 border-white pb-2 inline-block">Innovation</h4> {/* Added border-b-2 for underline */}
        <p className="text-lg mb-4">
          We stay ahead with cutting-edge technology and creative solutions to drive your success.
        </p>
        <Link to="/about" className="bg-blue-300 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-all mt-4">
          Learn More
        </Link>
      </div>
    </motion.div>
  </div>
</div>


    </div>
  );
};

export default Home;
